import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useContext } from 'react';
// eslint-disable-next-line no-restricted-imports
import { NavLink as RouterNavLink, useLocation } from 'react-router';
// eslint-disable-next-line no-restricted-imports
import type { Link as RouterLink, To } from 'react-router';
import useIsPublicView, { isPublicViewPath } from '../useIsPublicView';
import { LinkBaseUrlContext } from './LinkBaseUrlContext';
import { useIsExtension } from '../useIsExtension';

type SearchLocation = { search?: string } | string;

const getSearch = (location: SearchLocation): string => {
  if (typeof location === 'string') {
    return new URL(location, window.location.origin).search || '';
  }

  return location.search || '';
};

const isSearchEqual = (
  loc1: SearchLocation = '',
  loc2: SearchLocation = '',
): boolean => {
  const search1 = getSearch(loc1);
  const search2 = getSearch(loc2);

  if (search1 === search2) return true;

  const toSortedArray = (search: string) =>
    search.substring(1).split('&').toSorted();

  return _.isEqual(toSortedArray(search1), toSortedArray(search2));
};

const useShouldReloadDocument = (to: To) => {
  const isPublicView = useIsPublicView();

  try {
    const { pathname } =
      typeof to === 'string' ? new URL(to, window.location.href) : to;
    return !!pathname && isPublicViewPath(pathname) !== isPublicView;
  } catch (err) {
    return false;
  }
};

const joinHostAndPath = (host: string, path: string) =>
  `${host.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;

const useAbsoluteTo = (to: To) => {
  const linkBase = useContext(LinkBaseUrlContext);

  return !linkBase ||
    typeof to !== 'string' ||
    to.indexOf('://') > 0 ||
    to.indexOf('//') === 0
    ? to
    : joinHostAndPath(linkBase, to);
};

interface LinkProps
  extends Omit<React.ComponentProps<typeof RouterLink>, 'rel' | 'target'> {
  className?: string;
  activeClassName?: string;
  onlyActiveOnIndex?: boolean;
  onlyActiveOnSearchMatch?: boolean;
  newTab?: boolean | 'ugc';
}

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      className,
      onlyActiveOnSearchMatch,
      onlyActiveOnIndex,
      activeClassName,
      newTab,
      to,
      ...props
    },
    ref,
  ) => {
    const location = useLocation();
    const shouldReloadDocument = useShouldReloadDocument(to);
    const isExtension = useIsExtension();

    return (
      <RouterNavLink
        ref={ref}
        className={(navState) => {
          const isActive =
            navState.isActive &&
            (!onlyActiveOnSearchMatch || isSearchEqual(location, to));

          return (
            classNames(
              className,
              activeClassName && { [activeClassName]: isActive },
            ) || undefined
          );
        }}
        end={onlyActiveOnIndex}
        {...props}
        to={useAbsoluteTo(to)}
        reloadDocument={props.reloadDocument || shouldReloadDocument}
        target={newTab || isExtension ? '_blank' : undefined}
        {...(newTab
          ? { rel: newTab === 'ugc' ? 'nofollow noreferrer ugc' : 'noreferrer' }
          : {})}
      />
    );
  },
);

export default Link;
